import { DatabaseOutlined, FilterOutlined } from '@ant-design/icons'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import Moment from 'react-moment'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BackButton,
  Badge,
  ListPageLayout,
  PageContentDropdown,
  SectionLoadingState,
  Spacer,
  SubHeader,
} from '../../components'
import LiteratureReviewSearchTable from '../../components/LiteratureReviewSearchTable'
import { SearchTableRow } from '../../components/SearchTable'
import { BadgeColors, dateFormat } from '../../constants/constants'
import {
  useDataSources,
  useSearchReviewQueries,
  useSearchReviews,
} from '../../hooks'
import { routePaths } from '../RootPage'
import {
  searchReviewPanelTypes,
  searchReviewTabs,
} from '../SearchReviewDetailsPage/SearchReviewDetailsPage'
import {
  BadgesContainer,
  FloatLeftContainer,
} from './SearchReviewSearchQueryDocumentListPage'
const SearchReviewSearchQueryLiteratureReviewListPage = () => {
  const navigate = useNavigate()
  const { searchReviewId, searchId } = useParams()
  const [loadingIds, setLoadingIds] = useState<string[]>([])
  const {
    selectedSearchReview,
    exportSearchReviewSearchQueryCsv,
    refreshSearchReviewList,
  } = useSearchReviews({})

  const { searchQueryLoading, selectedSearchQuery, saveSearchQueryInclusion } = useSearchReviewQueries({
    searchReviewId: searchReviewId || '',
    searchQueryId: searchId || '',
  })

  const { getDataSourceTypeText } = useDataSources({
    preventFetch: true,
  })
  

  // refresh every 30 seconds for new updates on the search results summary status
  useEffect(() => {
    const interval = setInterval(() => {
      // we only want to refresh the list if there are no loadingIds
      if (loadingIds.length === 0) refreshSearchReviewList()
    }, 30000)
    return () => clearInterval(interval)
  }, [loadingIds.length, refreshSearchReviewList])

  const formattedData: SearchTableRow[] = useMemo(
    () =>
      selectedSearchQuery?.searchResults
        ? selectedSearchQuery?.searchResults.map((document, index) => {
            return {
              ...document,
              key: index.toString(),
              id: document?.id || '',
              title: document?.title,
              publicationName: document?.publicationName || '',
              authors: document?.authors,
              publicationDate: document?.publicationDate || '',
              included: selectedSearchReview?.includedSearchResults?.includes(
                document?.id || ''
              ),
              documentLink: document?.documentLink,
              language: document.language || '',
              summaryStatus: document?.summaryStatus || '',
            }
          })
        : [],
    [selectedSearchQuery, selectedSearchReview]
  )

  const sourceText = useMemo(
    () => getDataSourceTypeText(selectedSearchQuery?.searchSource),
    [selectedSearchQuery?.searchSource, getDataSourceTypeText]
  )

  const appendLoadingId = (recordId: string) => {
    const allLoadingIds = [...loadingIds, recordId]
    setLoadingIds(allLoadingIds)
  }

  const removeLoadingId = (recordId: string) => {
    const allLoadingIds = loadingIds.filter((id) => id !== recordId)
    setLoadingIds(allLoadingIds)
  }

  const handleOnSwitchClick = async (recordId: string) => {
    appendLoadingId(recordId)

    const handleResponse = () => {
      removeLoadingId(recordId)
    }

    try {
      await saveSearchQueryInclusion({
        recordIds: [recordId],
        navigate,
      })
      handleResponse()
    } catch (error) {
      console.error('axios fetch error', error)
      handleResponse()
    }
  }

  const handleExport = () => {
    exportSearchReviewSearchQueryCsv(
      selectedSearchReview?.id || '',
      selectedSearchQuery?.searchEvent || ''
    )
  }

  const handleOnRowClick = (recordId: string) => {
    navigate(
      `${routePaths.SEARCH_REVIEW_DOCUMENT}/${selectedSearchReview?.id}/${searchId}/${recordId}`
    )
  }

  if (searchQueryLoading) {
    return <SectionLoadingState />
  }

  return (
    <ListPageLayout>
      <SubHeader>
        <BackButton
          tooltipMessage={
            t('searchReviewSearchDetailsPage.backButtonTooltipMessage') || ''
          }
          url={`${routePaths.SEARCH_REVIEW_DETAILS}/${selectedSearchReview?.id}/${searchReviewPanelTypes.OVERVIEW}/${searchReviewTabs.QUERIES}`}
        />
        <div>
          {t('Query')}: {selectedSearchQuery?.searchText}
        </div>
        <Spacer />
        <FloatLeftContainer>
          <div>
            <Moment
              date={selectedSearchQuery?.searchDate}
              format={dateFormat.PRIMARY}
            />
          </div>
          <BadgesContainer>
            {sourceText && (
              <Badge
                color={BadgeColors.DATA_SOURCE}
                icon={<DatabaseOutlined />}
              >
                {sourceText}
              </Badge>
            )}
            {selectedSearchQuery?.searchParameters.date && (
              <Badge color={BadgeColors.FILTERS} icon={<FilterOutlined />}>
                {selectedSearchQuery?.searchParameters.date}
              </Badge>
            )}
          </BadgesContainer>
          <div>
            {t('Result total')}: {selectedSearchQuery?.searchResults.length}
          </div>
          <PageContentDropdown onExport={handleExport} />
        </FloatLeftContainer>
      </SubHeader>
      <LiteratureReviewSearchTable
        formattedData={formattedData}
        onClick={(recordId) => handleOnRowClick(recordId)}
        maxHeight="calc(100vh - 180px)"
        onSwitchClick={handleOnSwitchClick}
        loadingIds={loadingIds}
      />
    </ListPageLayout>
  )
}
export default SearchReviewSearchQueryLiteratureReviewListPage
